"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { BadgeCheck, Building2, CircleCheck as CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Compliance() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const quote = useRevealOnScroll<HTMLDivElement>();
  const footer = useRevealOnScroll<HTMLDivElement>();
  const certifications = [{
    name: "ISO 13485 Certified",
    issuer: "International Organization for Standardization",
    year: "2024",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/iso_certification_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/iso_certification_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/iso_certification_badge.png-wvc-sizes"
    },
    alt: "ISO 13485 medical device quality management badge"
  }, {
    name: "CE Marking Compliance",
    issuer: "European Union Regulatory Authority",
    year: "2024",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ce_marking_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ce_marking_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/ce_marking_badge.png-wvc-sizes"
    },
    alt: "CE marking compliance badge"
  }, {
    name: "FDA Registered Partner",
    issuer: "U.S. Food and Drug Administration",
    year: "2024",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/fda_registered_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/fda_registered_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/fda_registered_badge.png-wvc-sizes"
    },
    alt: "FDA registered partner badge"
  }, {
    name: "Global Quality Assurance",
    issuer: "International Medical Standards Board",
    year: "2024",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/quality_assurance_badge.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/quality_assurance_badge.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/quality_assurance_badge.png-wvc-sizes"
    },
    alt: "Global quality assurance badge"
  }];
  const trustSignals = [{
    icon: Building2,
    label: "Global Sourcing Network"
  }, {
    icon: ShieldCheck,
    label: "Regulatory Compliance"
  }, {
    icon: BadgeCheck,
    label: "Clinical Evaluation"
  }, {
    icon: Sparkles,
    label: "Quality Assured"
  }];
  return <section data-section-id="312" id="complianceandcertifications" className="relative overflow-hidden bg-background text-foreground py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/3 rounded-lg bg-primary/15 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-[0.18em] text-primary">
            Global Standards & Compliance
            <Sparkles className="size-3.5" />
          </span>
          <h2 className="mt-5 font-default text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-foreground">
            Committed to{" "}
            <em className="font-default italic text-primary">Global Excellence</em>.
          </h2>
          <p className="mt-6 font-default text-base sm:text-lg leading-relaxed text-muted-foreground">
            At MS Biotechnology, we bridge the gap between innovation and clinical safety. 
            Our rigorous evaluation and regulatory support ensure that every medical device 
            we distribute meets the highest international standards for patient care.
          </p>
        </div>

        {}
        <div ref={grid.ref} className={`mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 transition-all duration-700 ease-out delay-150 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {certifications.map((cert, i) => <Card key={cert.name} data-index={i} className="group relative border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
              <CardContent className="flex flex-col items-center px-6 pt-8 pb-7 text-center">
                <div className="flex h-24 w-24 items-center justify-center rounded-lg bg-secondary/60">
                  <img src={cert.image.src} data-wvc-srcset={cert.image.srcset} data-wvc-sizes={cert.image.sizes} alt={cert.alt} loading="lazy" decoding="async" className="h-20 w-20 object-contain" />
                </div>

                <h3 className="mt-6 font-default text-lg font-medium leading-snug text-foreground">
                  {cert.name}
                </h3>

                <p className="mt-2 font-default text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  {cert.issuer}
                </p>

                <Badge variant="outline" className="mt-5 gap-1.5 border-primary/30 bg-primary/10 px-2.5 py-1 font-default text-[10px] uppercase tracking-wider text-primary">
                  <CheckCircle2 className="size-3" />
                  Verified {cert.year}
                </Badge>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div ref={quote.ref} className={`mt-20 transition-all duration-700 ease-out delay-200 ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Card className="relative overflow-hidden border-0 bg-primary/90 shadow-xl">
            <CardContent className="relative px-8 py-12 sm:px-14 sm:py-16">
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-[0.18em] text-primary-foreground/80">
                  A Message from Our Leadership
                </span>
                <blockquote className="mt-6 font-default text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.25] tracking-tight text-primary-foreground">
                  "Compliance is the foundation of trust in the medical industry. 
                  We don't just distribute devices; we ensure that every product 
                  we source provides clinical value and meets the stringent 
                  regulatory requirements of our global partners."
                </blockquote>

                <div className="mt-8 flex items-center justify-center gap-3">
                  <SafeHydrate><Avatar className="size-12 ring-2 ring-primary-foreground/30">
                    <AvatarImage src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/leadership_avatar.png" alt="Executive Leadership at MS Biotechnology" />
                    <AvatarFallback className="bg-primary-foreground text-primary font-default">
                      MS
                    </AvatarFallback>
                  </Avatar></SafeHydrate>
                  <div className="text-left">
                    <p className="font-default text-base font-medium text-primary-foreground">
                      Executive Team
                    </p>
                    <p className="font-default text-[10px] uppercase tracking-[0.15em] text-primary-foreground/70">
                      MS Biotechnology Co., Ltd.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {}
        <div ref={footer.ref} className={`mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 transition-all duration-700 ease-out delay-300 ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {trustSignals.map((signal, i) => {
          const Icon = signal.icon;
          return <div key={signal.label} data-index={i} className="inline-flex items-center gap-2 font-default text-xs uppercase tracking-[0.14em] text-muted-foreground">
                <Icon className="size-3.5 text-primary" />
                <span>{signal.label}</span>
              </div>;
        })}
        </div>
      </div>
    </section>;
}